import { ClaimRecord, ClaimRecordStatus, EligibleProductsResult } from '@typesApp/ClaimRecord'
import { CartBundle, EligibleProductStatus, ProductBundle } from '@typesApp/Product'
import { getEligibleProductStatus, shouldCheckEligibleProducts } from '@utils/claimUtil'
import { isResourceNotFoundError } from '@utils/errorUtil'
import { isProductInsuranceEligible } from '@utils/isProductInsuranceEligible'

type ControllerService = {
  fetchClaimRecord: (id: string) => Promise<ClaimRecord>
  fetchCartBundle: () => Promise<CartBundle>
  fetchEligibleProducts: (products: ProductBundle[]) => Promise<EligibleProductsResult>

  onComplete?: (status: EligibleProductStatus) => Promise<void>
  onError?: (error?: unknown) => Promise<void>
}

type EligibleProductControllerReturn = {
  checkEligibility: () => Promise<EligibleProductStatus | undefined>
}

export const eligibleProductController = (
  service: ControllerService,
  claimRecordId: string | undefined
): EligibleProductControllerReturn => {
  const checkEligibility = async () => {
    try {
      const status = claimRecordId ? await checkWithClaimRecord(claimRecordId) : await checkWithCartBundle()
      if (service.onComplete) {
        await service.onComplete(status)
      }
      return status
    } catch (error) {
      if (service.onError) {
        await service.onError(error)
      } else {
        throw error
      }
    }
  }

  const checkWithClaimRecord = async (id: string): Promise<EligibleProductStatus> => {
    let claimRecord: ClaimRecord
    try {
      // re-fetch to ensure we have latest eligible products in claim record
      claimRecord = await service.fetchClaimRecord(id)
    } catch (error) {
      if (isResourceNotFoundError(error)) {
        return await checkWithCartBundle()
      }
      throw error
    }

    // skip if restricted
    if (claimRecord.status === ClaimRecordStatus.RESTRICTED) {
      return EligibleProductStatus.NA
    }

    // NOTE: claim record may not immediately reflect the status on re-fetch after cart change.
    // In addition, some brands (e.g. VDE) have insurer specific eligibility restrictions so
    // the products in the claim record may NOT be eligible (due to the insurer) but the cart
    // products may be eligible.

    // Hence, we need to use the cart items and check-insurance-eligibility API if the status
    // from the claim record is NOT_ELIGIBLE
    const eligibleProducts = claimRecord.eligibleProducts ?? []
    let status = getEligibleProductStatus(eligibleProducts)

    // In CA flow the eligibleProducts should not be empty but it may be if commerce
    // fails to call B/E cart update correctly. In this case we need to fetch cart to be sure.
    const shouldFetchCart = status === EligibleProductStatus.NOT_ELIGIBLE || eligibleProducts.length === 0
    if (shouldFetchCart && shouldCheckEligibleProducts(claimRecord.status)) {
      status = await checkWithCartBundle()
    }

    return status
  }

  const checkWithCartBundle = async (): Promise<EligibleProductStatus> => {
    const cartBundle = await service.fetchCartBundle()
    const products = cartBundle?.products ?? []

    let eligibleProducts = products

    // perform eligibility check only if we have cart products
    if (products.length > 0) {
      const result = await service.fetchEligibleProducts(products)
      const cartProducts = result?.eligibleProducts || []
      eligibleProducts = cartProducts.filter(isProductInsuranceEligible)
    }

    return eligibleProducts.length > 0 ? EligibleProductStatus.ELIGIBLE : EligibleProductStatus.NOT_ELIGIBLE
  }

  return {
    checkEligibility,
  }
}
